import { CommonModule } from "@angular/common";
import { Component, Input, OnInit, ViewChild } from "@angular/core";
import { FormsModule, ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup, Validators } from "@angular/forms";
import { MatButtonModule } from "@angular/material/button";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from "@angular/material/input";
import { MatMenuModule } from '@angular/material/menu';
import { MatProgressSpinnerModule } from "@angular/material/progress-spinner";
import { MatSnackBar } from "@angular/material/snack-bar";
import { MatTable, MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Router } from "@angular/router";
import { CurrencyMaskDirective } from "src/app/shared/directive/currency-mask.directive";
import { NgxMaskDirective, NgxMaskPipe, provideNgxMask } from "ngx-mask";
import { OrdemInclusao } from "src/app/core/model/ordem";
import { Titular } from "src/app/core/model/titular";
import { Globals } from "src/app/shared/helpers/globals";
import { DialogService } from "src/app/core/service/dialog.service";
import { OrdemBuilderService } from "src/app/core/service/ordem-builder.service";
import { NoSpecialCharsDirective } from 'src/app/shared/directive/no-special-chars.directive';
import { StringUtil } from "src/app/shared/util/string-util";
import { CustomValidators } from "src/app/shared/validators/custom-validators";
import { CpfCnpjService } from "../../../../../../core/service/cpf-cnpj.service";
import { SolicitacaoService } from '../../../../../../core/apis/solicitacao/solicitacao.service';
import { UserDataService } from "src/app/core/service/user-data.service";
import { UserData } from "src/app/core/service/model/userData.model";
import { SlaSelectorComponent } from "src/app/shared/components/sla-selector/sla-selector.component";
import { AcaoButtonsComponent } from "src/app/shared/components/acao-buttons/acao-buttons.component";
import { TipoOrdem } from "src/app/core/model/enum/tipo-ordem.enum";
import { ErrorHandlerService } from "src/app/core/service/error-handler.service";

@Component({
  selector: "app-atualizar-acao-form",
  templateUrl: "./atualizar-acao-form.component.html",
  styleUrls: ["./atualizar-acao-form.component.css"],
  providers: [provideNgxMask()],
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    NgxMaskDirective,
    MatButtonModule,
    MatTableModule,
    MatMenuModule,
    MatProgressSpinnerModule,
    CurrencyMaskDirective,
    NgxMaskPipe,
    MatTooltipModule,
    NoSpecialCharsDirective,
    SlaSelectorComponent,
    AcaoButtonsComponent
]
})
export class AtualizarAcaoFormComponent implements OnInit {

  @Input() tokenIam: string;
  @Input() nroProcesso: string;
  @Input() ordem: OrdemInclusao;

  form: UntypedFormGroup;

  @ViewChild(MatTable) table: MatTable<Titular>;
  displayedColumns: string[] = ["nome", "documento", "action"];
  editedTitular: Titular = null;

  titulares: Titular[];

  userData!: UserData;

  constructor(
    private router: Router,
    private globals: Globals,
    public snackBar: MatSnackBar,
    private cpfCnpjService: CpfCnpjService,
    private solicitacaoService: SolicitacaoService,
    private ordemBuilderService: OrdemBuilderService,
    private dialogService: DialogService,
    private userDataService: UserDataService,
    private errorHandler: ErrorHandlerService,
    fb: UntypedFormBuilder
  ) {
    this.form = fb.group({
      nroSla: ["120", Validators.required],
      titular: fb.group({
        nome: ["", [Validators.required, CustomValidators.validatorOnlyNumber]],
        documento: ["", Validators.required],
      }),
      vlrAcao: ["", [Validators.required, CustomValidators.validatorCurrencyZero]],
      nomeEdicao: ["", [Validators.required, CustomValidators.validatorOnlyNumber]],
      documentoEdicao: ["", Validators.required],
    });
  }

  ngOnInit() {
    this.titulares = this.ordem.titulares.map(t => ({ ...t }));
    this.form.patchValue({ vlrAcao: this.ordem.dadosOrdem.valorAnotacao });
    this.userData = this.userDataService.userData.value;
  }

  public addTitular() {
    let titular = this.buildTitular(this.nome.value, this.documento.value);

    if (this.isDocumentoValido(titular.documento)) {
      const docExistente = this.titulares.filter(element => element.documento == titular.documento);
      if (docExistente.length > 0) {
        this.globals.showSnackBar("Devedor já cadastrado!");
        return;
      }
      this.titulares.push(titular);

      this.nome.reset();
      this.documento.reset();

      this.table.renderRows();

      if (this.titulares.length === 1) {
        this.showInformativoTitularesDialog();
      }
    } else {
      this.globals.showSnackBar("Número do documento inválido!");
    }

  }

  private showInformativoTitularesDialog() {
    this.dialogService.showAlert(['Se houver mais devedores, adicionar nesse momento!']).subscribe();
  }

  private buildTitular(nome: string, documento: string): Titular {
    return {
      nome: nome.trim(),
      documento: StringUtil.removeNonNumber(documento)
    };
  }

  public startEdicao(titular: Titular) {
    this.nomeEdicao.setValue(titular.nome)
    this.documentoEdicao.setValue(titular.documento)
    this.editedTitular = titular;
  }

  public atualizarTitular(titular: Titular) {
    if (!this.isDocumentoValido(this.documentoEdicao.value)) {
        this.globals.showSnackBar("Número do documento inválido!");
        this.editedTitular = null;
        this.table.renderRows();
        return;
    }

    const index = this.titulares.indexOf(titular);
    if (index !== -1) {
        this.titulares[index].nome = this.nomeEdicao.value.trim();
        this.titulares[index].documento = this.documentoEdicao.value.replace(/\D/g, "");
        this.globals.showSnackBar("Informações do devedor atualizadas!", ["snackbar-success"]);
    }

    this.editedTitular = null;
    this.table.renderRows();
}

  public removerTitular(titular) {
    let index = this.titulares.indexOf(titular);
    this.titulares.splice(index, 1);
    this.table.renderRows();
  }

  public atualizarAcao() {
    if(!this.isFormValid()){
      this.touchForm();
      return;
    };
    let ordemUpdate: OrdemInclusao = {...this.ordem,
      horasSla: this.nroSla.value,
      cadastrador: {
        nome: this.userData.nome,
        documento: this.userData.documento
      },
      titulares: this.ordemBuilderService.processarTitulares(this.titulares, TipoOrdem.ACAO),
      dadosOrdem: {...this.ordem.dadosOrdem,
        valorAnotacao: this.vlrAcao.value
      }
    }

    this.globals.addToLoading("cadastrar-ordem");
    this.solicitacaoService.saveSolicitacao(ordemUpdate, this.tokenIam)
    .subscribe({
      next: response => {
        this.globals.subtractFromLoading();
        this.globals.showSnackBar("Atualização enviada com sucesso!", ["snackbar-success"]);
        this.router.navigate(["/ordem"]);
      },
      error: (error) => {
        this.globals.subtractFromLoading();
        this.errorHandler.handle(error);
      }
    });
  }

  touchForm() {
    if(this.isTitularesEmpty()){
      this.nome.setErrors({'titularesEmpty': true});
      this.nome.markAsTouched();
      this.documento.setErrors({'titularesEmpty': true});
      this.documento.markAsTouched();
    }
    if(this.vlrAcao.invalid) this.vlrAcao.markAsTouched();
    if(!this.hasTitularesChanged() && !this.hasValorChanged()) this.globals.showSnackBar("Não houve mudanças na ordem.")
  }

  public isFormValid(): boolean{
    return this.vlrAcao.valid && !this.isTitularesEmpty() && (this.hasValorChanged() || this.hasTitularesChanged());
  }

  private isTitularesEmpty(): boolean{
    return this.titulares.length == 0;
  }

  public cancelamentoDialog(): void {
    this.dialogService.showConfirmation(['Deseja cancelar a ação?']).subscribe(confirm => {
      if (confirm) {
        this.router.navigate(['/ordem']);
      }
    });
  }

  public disableAddDevedor(): boolean {
    return this.nome.hasError('required') || this.nome.hasError('valueOnlyNumberError')
      || this.documento.hasError('required');
  }

  public disableAtualizarTitular(): boolean {
    return !this.nomeEdicao.valid || !this.documentoEdicao.valid;
  }

  private hasValorChanged(): boolean {
    return this.vlrAcao.value !== this.ordem.dadosOrdem.valorAnotacao;
  }

  private hasTitularesChanged(): boolean {
    if (this.titulares.length !== this.ordem.titulares.length) {
      return true;
    }

    return this.titulares.some(titular => {
      const ordemTitular = this.ordem.titulares.find(o => o.documento === titular.documento);
      return !ordemTitular || titular.nome !== ordemTitular.nome;
    });
  }

  public isPessoaFisica(nroDocumento: string) {
    return nroDocumento.length == 11;
  }

  private isDocumentoValido(cpfCnpj: string) {
    return this.cpfCnpjService.validateCpfCnpj(cpfCnpj);
  }

  get nroSla() {
    return this.form.get("nroSla");
  }

  get nome() {
    return this.form.get("titular.nome");
  }

  get documento() {
    return this.form.get("titular.documento");
  }

  get nomeEdicao() {
    return this.form.get("nomeEdicao");
  }

  get documentoEdicao() {
    return this.form.get("documentoEdicao");
  }

  get vlrAcao() {
    return this.form.get("vlrAcao");
  }

}
